import { memo, useEffect, useRef } from 'react'
import * as React from 'react'
import TextField, { TextFieldProps } from '@mui/material/TextField'
import { useDebounce } from 'hooks/useDebounce'

const DEBOUNCE_TIME_DEFAULT = 500

type DebouncedTextFieldProps = TextFieldProps & {
  value: string
  debounceTime?: number
  maxLength?: number
  showRemainingCharsBelow?: number
  onTextChange: (val: string) => void
}

/**
 * DebouncedTextField is a component that wraps a TextField and debounces the onChange handler.
 * Useful for cases where the input value can be updated from different places (for example redux store and other user interactions)
 * in addition to user typing in the text field, and all updates must be synchronized.
 * The last value that was set is the one that will be displayed.
 */
export const DebouncedTextField = memo(({
  debounceTime, value, showRemainingCharsBelow, maxLength, onTextChange, ...rest
}: DebouncedTextFieldProps) => {
  const debounceTimeout = debounceTime || DEBOUNCE_TIME_DEFAULT
  const lastGlobalValue = useRef(value)
  const isLocalUpdatePending = useRef(false)

  const [localValue, setLocalValue] = useDebounce<string>(debounceTimeout, value, (val) => {
    if (val !== lastGlobalValue.current) {
      onTextChange(val)
      isLocalUpdatePending.current = false
    }
  })

  useEffect(() => {
    lastGlobalValue.current = value
  }, [value])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    isLocalUpdatePending.current = true
    setLocalValue(e.target.value)
  }

  useEffect(() => {
    if (!isLocalUpdatePending.current && value !== localValue) {
      setLocalValue(value)
    }
  }, [value, localValue, setLocalValue])

  let helperText = maxLength !== undefined ? `${maxLength - localValue.trim().length}` : undefined
  if (showRemainingCharsBelow && helperText && parseInt(helperText, 10) > showRemainingCharsBelow) {
    helperText = undefined
  }

  return (
    <TextField
      value={localValue}
      helperText={helperText}
      {...rest}
      onChange={handleChange}
    />
  )
})
